import React, { ComponentType, LazyExoticComponent } from 'react';

type ChunkLoadError = Error & {
  additionalMessage: string;
};

/**
 * Lazy import wrapper with retry and custom error for ChunkLoadError.
 *
 * @param factory - Function returning a Promise of a module with default export
 * @param retries - Number of retry attempts (default 4)
 * @param delay - Delay between retries in ms (default 1000)
 */
export function lazyWithCustomError<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 4,
  delay = 500
): LazyExoticComponent<T> {
  const createCustomError = async (err: Error, chunkUrl: string | null): Promise<never> => {
    let additionalMessage: string;
    if (chunkUrl) {
      try {
        const res = await fetch(chunkUrl, { method: 'GET' });
        additionalMessage = `Chunk failed to load at ${chunkUrl}. HTTP Status: ${res.status} ${res.statusText}`;
      } catch (fetchError: any) {
        additionalMessage = `Chunk failed to load at ${chunkUrl}. Unable to fetch: ${fetchError.message}`;
      }
    } else {
      additionalMessage = 'Chunk failed to load. No chunk URL found';
    }
    const customError: ChunkLoadError = new Error(err.message) as ChunkLoadError;
    customError.additionalMessage = additionalMessage;
    customError.stack = err.stack;
    throw customError;
  };

  return React.lazy(async () => {
    let attemptCount = retries;
    while (attemptCount >= 0) {
      try {
        return await factory();
      } catch (err: any) {
        if (err?.name !== 'ChunkLoadError') throw err;
        console.warn(`[lazyWithCustomError] Chunk load failed. Retries left: ${attemptCount}`, err);
        if (attemptCount === 0) {
          const chunkUrl = err.message.match(/https?:\/\/[^\s]+/)?.[0] || null;
          return createCustomError(err, chunkUrl);
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
        attemptCount--;
      }
    }
    throw new Error('Unreachable');
  });
}
